import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { EquippedSlot, ItemDto } from '../api/wardrobe';
import { itemIconPath } from '../compositor/assetPaths';
import { SegmentedControl } from '../components/SegmentedControl';
import { GRID_COLS, useLayoutTier, type LayoutTier } from '../shell/layoutTiers';

/** Exported for the shell test: the drawer chunks rows by the tier's column count, the JS twin of
 *  `--grid-cols` (a virtualised grid cannot read a CSS token). */
export function columnsForTier(tier: LayoutTier): number { return GRID_COLS[tier]; }
/** Columns when the drawer is a Studio right PANEL (Task 10): ~320 wide, the phone's width class,
 *  so the phone's four — the tier's 6/8 are sized for the wide centre column and gave 47-wide cells
 *  with every name clipped (seen in a real browser at 1024×768). The CSS twin is
 *  `.studio-right .item-drawer { --grid-cols: 4 }` in base.css; `itemDrawerRow.test.ts` holds
 *  the two equal. */
export const PANEL_COLS = 4;
// Row-height estimate fed to the virtualizer only (thumb + name + cell padding) — not a design
// token, just a layout hint the virtualizer uses to guess scroll extent before it
// measures real rows. CELL_ESTIMATE assumes the item name renders on ONE line; since the Studio's
// P1 fix `.item-cell-name` is a shared two-line clamp (`-webkit-line-clamp: 2`, no longer nowrap +
// ellipsis), and it reaches this drawer's phone cells too — which is safe here precisely because
// rows are MEASURED (`measureElement` below), so a two-line name grows its row instead of
// overflowing into the next absolutely-positioned one (the class of bug review finding I3 first
// guarded against). The estimate only ever costs a first-paint scroll-extent guess. ROW_GAP
// mirrors --sp-2, baked into the row's own box via CSS `padding-bottom`
// rather than a grid `row-gap`, since virtualized rows are independent absolutely-positioned
// elements, not one continuous grid — see `.item-drawer-row` in base.css.
// Dropped from 132 on 2026-08-20: the per-cell rarity tag is now visually hidden (it survives in
// the accessible name — see `.item-cell-rarity` in base.css), so a cell is thumb + one name line
// + padding. The virtualizer measures real rows and corrects itself either way; an estimate this
// far off only ever cost a visible scroll-extent jump on first paint.
const CELL_ESTIMATE = 116;
const ROW_GAP = 8; // raw-ok: JS layout hint mirroring --sp-2, not a CSS px literal
const ROW_ESTIMATE = CELL_ESTIMATE + ROW_GAP;

const RARITIES = ['common', 'uncommon', 'rare', 'radiant', 'mythic'] as const;
type RarityFilter = 'all' | (typeof RARITIES)[number];

function rarityLabel(rarity: string): string {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

// Six options, so the SegmentedControl's `flex: 1 1 auto` + wrap behaviour is doing real work
// here — this is the set that decided the control had to be --fs-1 rather than --fs-2.
const RARITY_OPTIONS: { key: RarityFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  ...RARITIES.map((r) => ({ key: r as RarityFilter, label: rarityLabel(r) })),
];

/* Inline glyphs, 24-unit grid, currentColor stroke — the same house convention as
   wardrobe/groupIcons.tsx and battle/elementIcons.tsx, and the same reason: a set this small,
   which has to read at --sp-5, is better drawn as strokes than ordered as raster art.
   raw-ok: SVG grid geometry in prose, not a CSS literal. */
function FiltersGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
  );
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

export type ItemDrawerProps = {
  /** Already server-filtered to owned + fitting the active slot (screen's job, not this
   *  component's) — the base list whenever the search field is empty. */
  items: ItemDto[];
  /** Owned items fitting this companion (stage-unlocked slots) across EVERY slot,
   *  not just the active one — the base list whenever the search field is non-empty (SCREENS.md
   *  drawer-search: "search results across all slots" overrides the slot filter; review
   *  follow-up). Stage-restricted items (e.g. a `held_left` item while hatchling) are excluded
   *  here by the screen, matching the "fits this companion" drawer contract — not shown
   *  locked-and-untappable, simply absent, same as they'd be absent from the active-slot list
   *  were their own slot selected. */
  allItems: ItemDto[];
  /** The full draft equipped-slots map — an item's "equipped" check and its tap target both key
   *  off `item.slot`, since a cross-slot search result's slot generally isn't the active one. */
  equipped: Record<string, EquippedSlot>;
  disabled: boolean;
  onTapItem: (item: ItemDto) => void;
  /** The context line under the category strip: the active group's label plus how many pieces
   *  are on in it ("Body · 3 pieces on"). It used to be the drawer's TITLE, at title weight,
   *  because the categories sat in a dock over the preview and nothing in the panel said which
   *  one was chosen. With the strip as the drawer's own first row the lit tab IS the title, so
   *  this is a quiet line that says the one thing the tab cannot. */
  title: string;
  /** Filter disclosure. Closed by default — the rarity segments and the cross-slot search cost
   *  the grid a whole row, and most visits are "put this on" rather than "find that one thing".
   *  NOT a detent: it does not move the preview and has no drag affordance (the sheet it used to
   *  belong to is gone — SCREENS.md "Wardrobe as a tab"). */
  filtersOpen: boolean;
  onToggleFilters: () => void;
  /** Rendered at the right of the sheet header — the screen puts Undo / Save Look here while the
   *  outfit is dirty. They used to float over the preview in their own overlay stack. */
  actions?: ReactNode;
  /** Category dock (2026-09-14): a one-word "where this goes" line under the name, shown when the
   *  active group spans more than one slot (the screen decides; this component only renders it).
   *  Null for no caption — the element is then absent, not empty, so a single-slot group's cells
   *  keep their old height. */
  captionFor?: (item: ItemDto) => string | null;
  /** The one-word captions of every slot this category covers, for the empty state (finding 6):
   *  an empty category otherwise says only that it is empty, never what it is FOR. The screen
   *  supplies them because it is the side that knows the group; this only renders them. */
  emptySlotCaptions?: string[];
  /** The category strip (`SlotRail`), rendered as the drawer's FIRST row, above the header. A
   *  node for the same reason `wornStrip` is: which group is active and what a tap does are the
   *  screen's business; the drawer owns only the row's place in the column. `null` renders
   *  nothing — on the desk (studio3, Task 10) the categories are a list in the Studio's left
   *  panel and the drawer starts at its header. */
  categories?: ReactNode | null;
  /** Row chunking override. The screen passes `PANEL_COLS` when the drawer renders as a Studio
   *  panel; otherwise the tier decides (`columnsForTier`). */
  columns?: number;
  /** The worn strip (finding 3), rendered between the header and the grid. A node rather than
   *  data because it is the SCREEN's business what is worn and what each action does — the drawer
   *  only owns the space it occupies above the scroller. */
  wornStrip?: ReactNode;
};

/**
 * SCREENS.md "Wardrobe" drawer states (ready / drawer-search / drawer-empty), §11.4: a 4-across
 * virtualized grid (rows virtualized, not cells — a whale's wardrobe is thousands of items),
 * a rarity filter + a search field. "owned" and "slot" filtering already happened
 * upstream (WardrobeScreen passes in both `items`, this slot's owned+fitting items, and
 * `allItems`, every slot's — see their own doc comments above) — "newest" is skipped per
 * task-14-brief.md: ItemDto has no acquiredAt field to sort by, and inventing one would be
 * fabricating data the server doesn't send (flagged in the Task 14 report).
 *
 * A PULL-UP SHEET WITH TWO DETENTS (2026-08-20). The drawer used to be a fixed band under a fixed
 * slot rail under a fixed preview — four stacked bands, all competing for one phone column. It is
 * now a sheet: at PEEK it shows its header and two item rows, and the preview gets everything
 * else; at FULL it shows the filter chrome and the grid. The header carries the slot title and,
 * while dirty, Undo / Save Look — which used to float in an absolutely-positioned overlay stack
 * across the preview's top control row, squeezed between Close and Looks.
 *
 * DELIBERATELY NOT A `BottomSheet`. That component is a Radix modal dialog: it traps focus, dims
 * what is behind it and closes on Escape. This drawer must stay open while the player pans and
 * zooms the pet above it, so it stays an ordinary in-flow flex child of `.wardrobe` — which also
 * keeps the virtualizer's scroll container exactly where it was.
 *
 * WHY THE FILTERS ONLY EXIST AT FULL. Peek is for "put this on"; filtering is a deliberate act
 * that deserves the room. The one hazard is a filter left active while collapsed, which would
 * silently narrow the grid with nothing on screen to say so — so the header shows a "Filtered"
 * chip with a clear action in exactly that case, and never otherwise.
 */
export function ItemDrawer({
  items, allItems, equipped, disabled, onTapItem, title, filtersOpen, onToggleFilters, actions, captionFor,
  emptySlotCaptions, wornStrip, categories, columns: columnsOverride,
}: ItemDrawerProps) {
  const [search, setSearch] = useState('');
  const [rarity, setRarity] = useState<RarityFilter>('all');
  const parentRef = useRef<HTMLDivElement>(null);

  const trimmedSearch = search.trim();
  const searching = trimmedSearch.length > 0;
  // A non-empty search spans every slot (overrides the active-slot filter); clearing it reverts
  // to the active slot's own items. Rarity still composes with either base list.
  const base = searching ? allItems : items;

  const filtered = useMemo(() => {
    const q = trimmedSearch.toLowerCase();
    return base.filter(
      (i) => (rarity === 'all' || i.rarity === rarity) && (!q || i.name.toLowerCase().includes(q)),
    );
  }, [base, trimmedSearch, rarity]);

  const tierColumns = columnsForTier(useLayoutTier());
  const columns = columnsOverride ?? tierColumns;
  const rows = useMemo(() => chunk(filtered, columns), [filtered, columns]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_ESTIMATE,
    // The row's height is MEASURED, not asserted (2026-08-20). `.item-cell` is now square
    // (`aspect-ratio: 1`), so a row's real height is its column width — a number that changes with
    // the viewport and that no constant can be right about. Left as a fixed `virtualRow.size` it
    // showed as a fat dead gap under every row on a phone. `|| ROW_ESTIMATE` matters: jsdom has no
    // layout engine and reports 0 here, which would collapse every row and starve the unit tests
    // of anything to assert on, so the estimate stays the fallback.
    measureElement: (el) => el.getBoundingClientRect().height || ROW_ESTIMATE,
    // I2 (review, Task 14 fix round): @tanstack/react-virtual defaults to react-dom's
    // `flushSync` for its own re-renders (scroll-jank prevention). `flushSync` forces an
    // immediate, synchronous render outside React's normal batching — which also means outside
    // whatever `act()` scope a test happens to have open at that exact moment, since the
    // virtualizer's own measurement/init lifecycle triggers it independently of anything this
    // component does. Traced empirically to this exact call (a scratch repro that warned on a
    // bare mount, before any interaction, iff it waited for drawer content) — not to
    // WardrobeScreen's own state, react-query, or the fix round's other changes. A drawer this
    // size has no scroll-jank case worth `flushSync`'s cost (a forced synchronous render on
    // every scroll-driven range change) in the first place.
    useFlushSync: false,
    // Generous overscan: keeps small (test-fixture- or early-game-sized) lists fully rendered
    // even when the scroll container reports zero height (e.g. jsdom, or before first layout),
    // while a real thousand-item wardrobe still only renders a small visible window.
    overscan: 8,
  });

  const narrowed = rarity !== 'all' || searching;

  return (
    <div className="item-drawer">
      {categories}
      <div className="item-drawer-head">
        {/* The grab handle IS the detent control. A real <button> rather than a drag-only
            affordance: a pointer drag is not reachable by keyboard, and jsdom has no
            PointerEvent implementation, so a drag-only handle would be neither accessible nor
            testable. `aria-expanded` is the state; the bar inside is decoration. */}
        <span className="item-drawer-title">{title}</span>
        {/* The sheet's grab handle is gone with the sheet (SCREENS.md "Wardrobe as a tab"). What
            is left is an ordinary disclosure for the filter chrome: closed by default, which is
            what buys the grid its third row. A real button with `aria-expanded`, never a drag —
            a pointer drag is not reachable by keyboard and jsdom has no PointerEvent. */}
        {/* The LABEL is the same string it used to render as text, so every existing selector —
            unit tests, two e2e specs — resolves unchanged; only the paint moved. */}
        <button
          type="button"
          className="drawer-filters-toggle"
          aria-expanded={filtersOpen}
          aria-label={filtersOpen ? 'Hide filters' : 'Filters'}
          onClick={onToggleFilters}
        >
          <FiltersGlyph />
        </button>
        {/* Only while collapsed: a filter narrowing the grid with the filter chrome off-screen is
            invisible state, so it gets said out loud here and can be cleared in one tap. */}
        {!filtersOpen && narrowed && (
          <button
            type="button"
            className="chip drawer-filtered-chip"
            onClick={() => { setRarity('all'); setSearch(''); }}
          >
            Filtered &times;
          </button>
        )}
        {actions && <div className="item-drawer-actions">{actions}</div>}
      </div>

      {filtersOpen && (
        <div className="item-drawer-filters">
          <SegmentedControl
            label="Filter by rarity"
            options={RARITY_OPTIONS}
            value={rarity}
            onChange={setRarity}
          />
          <input
            type="search"
            className="drawer-search"
            aria-label="Search items"
            placeholder="Search…"
            value={search}
            disabled={disabled}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {wornStrip}

      {disabled && <p className="form-note drawer-offline-note">Reconnecting… equipping is paused until you're back online.</p>}

      {base.length === 0 ? (
        searching ? (
          <p className="drawer-empty">No results</p>
        ) : (
          /* Not a dead end. The sentence is unchanged (SCREENS.md pins the copy); what is new is
             what the category HOLDS and one way onward. The link goes to /world rather than to a
             shop: no shop in the pack is "the cosmetics shop" — the seven of them each sell their
             own land's pieces, and naming one here would wire a screen to a content code that a
             pack is free to rename or drop. */
          <div className="drawer-empty">
            <span className="drawer-empty-title">Nothing fits here yet</span>
            {emptySlotCaptions && emptySlotCaptions.length > 0 && (
              <>
                <span>This category covers:</span>
                <ul className="drawer-empty-slots">
                  {emptySlotCaptions.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </>
            )}
            <Link className="drawer-empty-link" to="/world">Find more out in the world</Link>
          </div>
        )
      ) : filtered.length === 0 ? (
        <p className="drawer-empty">No results</p>
      ) : (
        <div ref={parentRef} className="item-drawer-scroll">
          <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                className="item-drawer-row"
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {rows[virtualRow.index].map((item) => {
                  // Keyed off the ITEM'S OWN slot, not a single "active slot" — a cross-slot
                  // search result's equipped check (and its tap target, via onTapItem) must
                  // resolve against whatever's equipped in item.slot specifically.
                  const isEquipped = equipped[item.slot]?.itemCode === item.code;
                  return (
                    <button
                      key={item.code}
                      type="button"
                      className={`item-cell rarity-${item.rarity}${isEquipped ? ' item-cell-worn' : ''}`}
                      disabled={disabled}
                      onClick={() => onTapItem(item)}
                    >
                      <img src={itemIconPath(item.code)} alt="" className="item-cell-thumb" />
                      <span className="item-cell-name">{item.name}</span>
                      {(() => { const cap = captionFor?.(item); return cap ? <span className="item-cell-caption">{cap}</span> : null; })()}
                      {/* §11.8 "never colour alone": the tier is a PIP COUNT (image_assets.md §7,
                          amended 2026-08-20) — a non-colour cue on its own — so this label is
                          visually hidden by `.item-cell > .item-cell-rarity` and survives only in
                          the cell's accessible name. Kept in the markup for exactly that reason. */}
                      <span className="item-cell-rarity">{rarityLabel(item.rarity)}</span>
                      {/* The word is the accessible name's ("Acorn Cap, uncommon, Equipped"); the
                          disc is what the eye gets. See `.item-cell-badge` in base.css for why it
                          stopped being a pill. */}
                      {isEquipped && (
                        <span className="item-cell-badge">
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none"
                            stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12l5 5 9-10" />
                          </svg>
                          <span className="sr-only">Equipped</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
